/**
 * Stock Hold Timeout dialog — create / edit config
 */

import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { StockHoldTimeoutConfigItem } from '../../stock-hold-timeout.component';
import { StockHoldTimeoutFeatureService } from '../../services/stock-hold-timeout-feature.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';
import {
  CONFIGURABLE_STOCK_HOLD_CATEGORIES,
  getStockHoldCategoryLabelKey,
} from '@app/shared/utils/stock-hold-timeout.util';

interface CategoryOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-stock-hold-timeout-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    InputNumberModule,
    SelectModule,
    CheckboxModule,
    MessageModule,
    AppButtonComponent,
  ],
  templateUrl: './stock-hold-timeout-dialog.component.html',
  styleUrl: './stock-hold-timeout-dialog.component.css',
})
export class StockHoldTimeoutDialogComponent implements OnInit {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly translateService = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly featureService = inject(StockHoldTimeoutFeatureService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly messageService = inject(MessageService);

  showErrors = signal(false);
  isEditMode = false;
  saving = signal(false);
  errorMessage = signal<string | null>(null);
  categoryOptions: CategoryOption[] = [];

  configForm: FormGroup = this.fb.group({
    holdCategory: ['', [Validators.required]],
    timeoutMinutes: [null, [Validators.required, Validators.min(1)]],
    enabled: [true],
  });

  ngOnInit(): void {
    this.categoryOptions = CONFIGURABLE_STOCK_HOLD_CATEGORIES.filter(
      (code) => !(this.config.data?.existingCategories as string[] | undefined)?.includes(code),
    ).map((code) => ({
      value: code,
      label: this.translateService.instant(getStockHoldCategoryLabelKey(code)),
    }));

    this.configForm.valueChanges.subscribe(() => this.errorMessage.set(null));

    if (this.config.data?.config) {
      this.isEditMode = true;
      const item: StockHoldTimeoutConfigItem = this.config.data.config;
      this.configForm.patchValue({
        holdCategory: item.holdCategory,
        timeoutMinutes: item.timeoutMinutes,
        enabled: item.enabled,
      });
      this.configForm.get('holdCategory')?.disable();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.configForm.invalid) {
      this.showErrors.set(true);
      this.configForm.markAllAsTouched();
      return;
    }

    if (this.saving()) {
      return;
    }

    this.errorMessage.set(null);
    this.saving.set(true);

    const raw = this.configForm.getRawValue();
    const payload: StockHoldTimeoutConfigItem = {
      id: this.config.data?.config?.id,
      holdCategory: raw.holdCategory,
      timeoutMinutes: raw.timeoutMinutes,
      enabled: raw.enabled ?? true,
    };

    const request$ = this.isEditMode
      ? this.featureService.updateConfig(payload)
      : this.featureService.createConfig(payload);

    request$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            this.isEditMode
              ? 'stock_hold_timeout.messages.update_success'
              : 'stock_hold_timeout.messages.create_success',
          ),
        });
        this.dialogRef.close(true);
      },
      error: (error: unknown) => {
        this.saving.set(false);
        const errorMessage = extractErrorMessage(error);
        this.errorMessage.set(
          this.errorTranslatorService.translate(
            errorMessage,
            'stock_hold_timeout.messages.save_error',
          ),
        );
      },
    });
  }
}
